/**
 * CureElectionField — the seller's cure-right election, section 7(d), on a
 * Wisconsin offer (legal copy: contingency-notices-copy.v1.md §11,
 * `WI.offer.cure.*`).
 *
 * Shown only on a Wisconsin offer (residential, condo or vacant land) that
 * selects at least one curable contingency (`cureElectionAsked`). The buyer
 * must choose one; neither is preselected or recommended:
 *   - "Seller may cure" (`seller_cure_right: 'cure'`) with the cure period
 *     `seller_cure_days` (1–365, no default);
 *   - "No cure right" (`seller_cure_right: 'no_cure'`).
 *
 * Rendered by the offer wizard's Terms step and both counter forms. The value
 * lives in the form's `contingencies` object; see `applyCureElection` and
 * `serializeContingencies` in contingencyCatalog.js.
 *
 * Props:
 *   - category: the Wisconsin offer category (`cureElectionCategory`), or null
 *   - contingencies: form state
 *   - onChange(nextContingencies)
 *   - disabled
 *   - serverErrors: `{seller_cure_right, seller_cure_days}` messages from a 422
 */
import React, { useId } from 'react';
import {
  CURE_COPY as COPY,
  CURE_DAYS_INVALID_MESSAGE,
  CURE_RIGHT_CURE,
  CURE_RIGHT_NO_CURE,
  MAX_DEADLINE_DAYS,
  MIN_DEADLINE_DAYS,
  SELLER_CURE_DAYS_KEY,
  SELLER_CURE_RIGHT_KEY,
  applyCureElection,
  cureElectionAsked,
  getCureElection,
  parseDeadlineDays,
} from './contingencyCatalog';
import styles from './CureElectionField.module.css';

export default function CureElectionField({
  category,
  contingencies,
  onChange,
  disabled = false,
  serverErrors = null,
}) {
  const id = useId();
  const questionId = `${id}-question`;
  const helpId = `${id}-help`;
  const daysId = `${id}-days`;
  const daysHelpId = `${id}-days-help`;
  const daysErrorId = `${id}-days-error`;
  const missingId = `${id}-missing`;

  if (!cureElectionAsked(category, contingencies)) return null;

  const right = getCureElection(contingencies);
  const rawDays = contingencies?.[SELLER_CURE_DAYS_KEY];
  const daysText = rawDays === undefined || rawDays === null ? '' : String(rawDays);
  const days = parseDeadlineDays(daysText);
  let daysError = null;
  if (right === CURE_RIGHT_CURE) {
    if (days === undefined) daysError = CURE_DAYS_INVALID_MESSAGE;
    else if (days === null) daysError = COPY.missing;
  }
  const rightServerError = serverErrors?.[SELLER_CURE_RIGHT_KEY] || null;
  const daysServerError = serverErrors?.[SELLER_CURE_DAYS_KEY] || null;

  const choose = (value) => onChange(applyCureElection(contingencies, value));
  const setDays = (text) => onChange({ ...(contingencies || {}), [SELLER_CURE_DAYS_KEY]: text });

  return (
    <fieldset
      className={styles.election}
      data-testid="cure-election"
      data-election={right || undefined}
      aria-describedby={helpId}
    >
      <legend className={styles.title}>
        {COPY.title}
        <span className={styles.required} aria-hidden="true"> *</span>
      </legend>
      <p id={questionId} className={styles.question}>
        {COPY.question}
      </p>
      <p id={helpId} className={styles.help}>
        {COPY.help(category)}
      </p>

      <div
        role="radiogroup"
        aria-labelledby={questionId}
        aria-required="true"
        aria-describedby={!right ? missingId : undefined}
        className={styles.options}
      >
        <label className={`${styles.option} ${right === CURE_RIGHT_CURE ? styles.optionOn : ''}`}>
          <input
            type="radio"
            name={`${id}-cure`}
            value={CURE_RIGHT_CURE}
            checked={right === CURE_RIGHT_CURE}
            onChange={() => choose(CURE_RIGHT_CURE)}
            disabled={disabled}
            data-testid="cure-option-cure"
          />
          <span>{COPY.optionCure}</span>
        </label>

        {right === CURE_RIGHT_CURE && (
          <div className={styles.days} data-testid="cure-days-field">
            <label htmlFor={daysId} className={styles.daysLabel}>
              {COPY.daysLabel}
              <span className={styles.required} aria-hidden="true"> *</span>
            </label>
            <input
              id={daysId}
              className={styles.daysInput}
              type="number"
              inputMode="numeric"
              min={MIN_DEADLINE_DAYS}
              max={MAX_DEADLINE_DAYS}
              step="1"
              value={daysText}
              onChange={(e) => setDays(e.target.value)}
              disabled={disabled}
              required
              aria-describedby={[daysHelpId, daysError && daysErrorId].filter(Boolean).join(' ')}
              aria-invalid={Boolean(daysError || daysServerError) || undefined}
              data-testid="cure-days"
            />
            <p id={daysHelpId} className={styles.hint}>{COPY.daysHelp}</p>
            {daysError && (
              <p id={daysErrorId} className={styles.error} role="alert">{daysError}</p>
            )}
            {daysServerError && (
              <p className={styles.error} role="alert" data-testid={`contingency-error-${SELLER_CURE_DAYS_KEY}`}>
                {daysServerError}
              </p>
            )}
          </div>
        )}

        <label className={`${styles.option} ${right === CURE_RIGHT_NO_CURE ? styles.optionOn : ''}`}>
          <input
            type="radio"
            name={`${id}-cure`}
            value={CURE_RIGHT_NO_CURE}
            checked={right === CURE_RIGHT_NO_CURE}
            onChange={() => choose(CURE_RIGHT_NO_CURE)}
            disabled={disabled}
            data-testid="cure-option-no-cure"
          />
          <span>{COPY.optionNoCure}</span>
        </label>
      </div>

      {!right && (
        <p id={missingId} className={styles.missing} data-testid="cure-missing">
          {COPY.missing}
        </p>
      )}
      {rightServerError && (
        <p className={styles.error} role="alert" data-testid={`contingency-error-${SELLER_CURE_RIGHT_KEY}`}>
          {rightServerError}
        </p>
      )}
    </fieldset>
  );
}
